/**
 * db.js — Central Firebase Realtime Database service
 *
 * Single source of truth for the DB base URL and all read/write operations.
 * All components import from here — nobody reads process.env directly.
 *
 * Environment switching is automatic:
 *   npm start        → .env.development → TEST  (mathzoo-b798d)
 *   npm run build    → .env.production  → PROD  (mathzoojr-prod)
 */

const BASE_URL = process.env.REACT_APP_FIREBASE_DATABASE_URL;

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Converts an email address into a Firebase-safe key.
 * Firebase keys cannot contain dots or @ symbols.
 * e.g. "user@example.com" → "user_at_example_dot_com"
 */
const emailToKey = (email) =>
  email.toLowerCase()
    .replace(/\./g, '_dot_')
    .replace(/@/g, '_at_');

// ─── Readers ────────────────────────────────────────────────────────────────

/**
 * Fetch all data for a user by their userId.
 * Returns the raw Firebase node or null on error.
 */
export const getUser = async (userId) => {
  const response = await fetch(`${BASE_URL}/users/${userId}.json`);
  if (!response.ok) {
    throw new Error(`getUser failed — HTTP ${response.status}`);
  }
  return response.json();
};

/**
 * Look up a userId from the emailIndex, then fetch that user's data.
 * Returns { userId, userData } or null if not found.
 */
export const getUserByEmail = async (email) => {
  const key = emailToKey(email);
  const indexResponse = await fetch(`${BASE_URL}/emailIndex/${key}.json`);
  const userId = await indexResponse.json();

  if (!userId || typeof userId !== 'string') {
    return null;
  }

  const userResponse = await fetch(`${BASE_URL}/users/${userId}.json`);
  const userData = await userResponse.json();

  if (!userData || userData.error) {
    return null;
  }

  return { userId, userData };
};

// ─── Writers ────────────────────────────────────────────────────────────────

/**
 * Patch (partial update) a user node.
 * Only the provided fields are updated — other fields are left untouched.
 */
export const updateUser = async (userId, data) => {
  const response = await fetch(`${BASE_URL}/users/${userId}.json`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`updateUser failed — HTTP ${response.status}`);
  }
  return response.json();
};

// ─── Debug ───────────────────────────────────────────────────────────────────

/**
 * Returns the active DB base URL — useful for logging/debugging.
 * Call dbInfo() in the browser console to confirm which DB is active.
 */
export const dbInfo = () => {
  console.log(`🔥 Active DB: ${BASE_URL}`);
  return BASE_URL;
};
